import { Settings } from 'lucide-react';
import { lazy } from 'react';
import { msg } from '@/lib/i18n';
import { registerInboxSource } from '@/lib/inbox';
import { registerInvalidation } from '@/lib/realtime';
import { registerEntityHref } from '@/ui';
import { registerEntityTab } from '../entityTabs';
import { defineModule } from '../types';
import { adminKeys } from './api';
import { ADMIN_NAV_PERMISSIONS, AREAS } from './labels';
import { newsInbox } from './news/feed';
import { adminRoutes } from './routes';

/**
 * Admin (IA §2 Manage › Admin): company, projects, people, roles and grants, API keys,
 * settings with provenance, news, the access log, AI usage and policy, system health — and,
 * for Toecan staff, companies and their commercial modules.
 *
 * The nav entry shows for people with an admin area beyond reading the project list
 * (`ADMIN_NAV_PERMISSIONS`): coaches, dispatchers, installers … see projects in the scope picker
 * and reach /admin/projects by its own route permission (deep links), without an Admin entry.
 *
 * News written here reaches the people it is for: unread posts in the bell (`newsInbox`) and on
 * Home (the home module's news card).
 */
export const adminModule = defineModule({
  key: 'admin',
  routes: adminRoutes,
  nav: [
    {
      key: 'admin',
      label: msg('Admin'),
      description: msg('How is the account set up?'),
      icon: Settings,
      to: '/admin',
      section: 'manage',
      order: 110,
      module: 'admin',
      permission: ADMIN_NAV_PERMISSIONS,
      match: ['/admin'],
      children: AREAS.map((a) => ({ key: a.key, label: a.label, to: a.to, permission: a.permission })),
    },
  ],
  setup() {
    registerInboxSource(newsInbox);
    // Canonical pages of the admin entities (links from actions, search and the assistant).
    registerEntityHref('user', (id) => `/admin/users/${id}`);
    registerEntityHref('project', (id) => `/admin/projects/${id}`);
    registerEntityHref('role', (id) => `/admin/roles/${id}`);
    registerEntityHref('company', (id) => `/admin/companies/${id}`);

    // Project page tabs (the old 9-tab project modal). Other modules add theirs the same way
    // (tachograph cards, integrations …).
    registerEntityTab('project', { key: 'details', label: msg('Details'), order: 10, component: lazy(() => import('./projects/tabs/ProjectDetailsTab')) });
    registerEntityTab('project', {
      key: 'modules',
      label: msg('Modules'),
      order: 20,
      component: lazy(() => import('./projects/tabs/ProjectModulesTab')),
    });
    registerEntityTab('project', {
      key: 'settings',
      label: msg('Settings'),
      order: 30,
      permission: 'core.setting.read',
      component: lazy(() => import('./projects/tabs/ProjectSettingsTab')),
    });
    registerEntityTab('project', {
      key: 'people',
      label: msg('People'),
      order: 40,
      permission: 'core.user.read',
      component: lazy(() => import('./projects/tabs/ProjectPeopleTab')),
    });

    // Person page tabs.
    registerEntityTab('user', { key: 'access', label: msg('Access'), order: 10, component: lazy(() => import('./users/tabs/UserAccessTab')) });
    registerEntityTab('user', {
      key: 'permissions',
      label: msg('What they can do'),
      order: 20,
      permission: 'core.role.manage',
      component: lazy(() => import('./users/tabs/UserPermissionsTab')),
    });
    registerEntityTab('user', { key: 'details', label: msg('Details'), order: 30, component: lazy(() => import('./users/tabs/UserDetailsTab')) });

    // IA §3: the vehicle page shows its effective settings and where each comes from.
    registerEntityTab('vehicle', {
      key: 'settings',
      label: msg('Settings'),
      order: 90,
      permission: 'core.setting.read',
      module: 'admin',
      component: lazy(() => import('./settings/VehicleSettingsTab')),
    });

    registerInvalidation('company', (n) => [adminKeys.company(n.subject_id ?? ''), ['admin', 'companies'], ['admin', 'settings'], ['admin', 'ai-usage']]);
    registerInvalidation('project', (n) => [adminKeys.project(n.subject_id ?? ''), ['admin', 'projects'], ['admin', 'settings']]);
    registerInvalidation('user', (n) => {
      const id = n.subject_id ?? '';
      return [adminKeys.user(id), adminKeys.userRoles(id), adminKeys.effective(id), ['admin', 'users'], ['admin', 'assignments']];
    });
    registerInvalidation('role', (n) => [adminKeys.role(n.subject_id ?? ''), adminKeys.roles, ['admin', 'assignments']]);
    registerInvalidation('api_key', () => [['admin', 'api-keys']]);
    registerInvalidation('news_post', () => [['admin', 'news']]);
    // Setting changes name the level they were made at (company, project, group or vehicle).
    for (const level of ['group', 'vehicle']) {
      registerInvalidation(level, (n) => (n.type === 'core.setting.changed' ? [['admin', 'settings']] : []));
    }
  },
});
